/**
 * Amazon Pay on the order step (normal checkout, not express).
 *
 * Markup contract, inside the order form:
 *   <div x-data="amazonPayCheckout({ config: { merchantId, ledgerCurrency, sandbox, checkoutLanguage,
 *                                               productType, placement },
 *                                     errorText: '…' })">
 *     <p class="errors_wrapper" x-show="error" x-text="error"></p>
 *
 * The order form does not send itself for Amazon Pay: the hook runs after the form is
 * valid (terms ticked), lets the shop keep the confirmed terms in the session and sign the
 * checkout session for the basket as it is now (a voucher may have changed it since the
 * page loaded), then hands the customer to Amazon with amazon.Pay.initCheckout. Amazon processes the order and
 * returns to cl=order&fnc=amazonpay_continue, which finishes it in the shop. Express
 * checkout has no component here; its form goes to execute(), which redirects itself.
 */
import { postAction } from '../shell/request.js';
import { loadScript } from '../shell/loadExternal.js';

const SDK_URL = 'https://static-eu.payments-amazon.com/checkout.js';

function amazonPaySdk() {
    if (window.amazon?.Pay) {
        return Promise.resolve(window.amazon.Pay);
    }

    return loadScript(SDK_URL).then(() => {
        if (!window.amazon?.Pay) {
            throw new Error('Amazon Pay SDK unavailable');
        }

        return window.amazon.Pay;
    });
}

document.addEventListener('alpine:init', () => {
    Alpine.data('amazonPayCheckout', ({ config, errorText }) => ({
        error: '',

        init() {
            // loaded before the click, so the hand-over does not wait for the network
            amazonPaySdk().catch(() => {});
            this.addBeforeSubmit(() => this.confirm());
        },

        async confirm() {
            this.error = '';

            try {
                const sdk = await amazonPaySdk();
                const body = new FormData(this.$el.closest('form'));
                // the shop reads post before get: the form's own fnc=execute must not reach it
                body.set('cl', 'order');
                body.set('fnc', 'amazonpay_prepare');

                const answer = await postAction(`/${URLSEGMENT}index.php?cl=order&fnc=amazonpay_prepare`, body);
                if (!answer?.createCheckoutSessionConfig) {
                    throw new Error('Amazon Pay checkout session not prepared');
                }

                // the form stays locked until Amazon's page is there
                this.redirecting = true;
                sdk.initCheckout({ ...config, createCheckoutSessionConfig: answer.createCheckoutSessionConfig });
            } catch (error) {
                this.redirecting = false;
                this.error = errorText;
            }

            return false;
        },
    }));
});
